import React, { useEffect, useRef } from 'react';
import { FiInfo, FiX } from 'react-icons/fi';
import './MuteUserModal.css';

/**
 * MuteUserModal - Star Citizen themed confirmation modal for muting users
 *
 * Props:
 * - isOpen (boolean): Controls modal visibility
 * - userName (string): Display name of user to mute
 * - userId (string): UUID of user to mute
 * - onConfirm (function): Callback when user confirms mute
 * - onCancel (function): Callback when user cancels
 *
 * Features:
 * - Escape key to close
 * - Click overlay to close
 * - Focus trap (focuses confirm button on open)
 * - Prevents body scroll when open
 * - ARIA labels for accessibility
 */
export default function MuteUserModal({ isOpen, userName, userId, onConfirm, onCancel }) {
    const confirmButtonRef = useRef(null);
    const modalRef = useRef(null);

    // Focus trap and escape key handler
    useEffect(() => {
        if (!isOpen) return;

        // Focus confirm button on open
        if (confirmButtonRef.current) {
            confirmButtonRef.current.focus();
        }

        // Prevent body scroll
        document.body.style.overflow = 'hidden';

        // Escape key handler
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                console.log('MuteUserModal: Escape key pressed, closing modal');
                onCancel();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onCancel]);

    // Focus trap within modal
    useEffect(() => {
        if (!isOpen || !modalRef.current) return;

        const modal = modalRef.current;
        const focusableElements = modal.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        const handleTab = (e) => {
            if (e.key !== 'Tab') return;

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        };

        modal.addEventListener('keydown', handleTab);

        return () => {
            modal.removeEventListener('keydown', handleTab);
        };
    }, [isOpen]);

    const handleConfirmClick = () => {
        console.log('MuteUserModal: User confirmed mute for userId:', userId);
        onConfirm(userId);
    };

    const handleCancelClick = () => {
        console.log('MuteUserModal: User cancelled mute');
        onCancel();
    };

    const handleOverlayClick = () => {
        console.log('MuteUserModal: Overlay clicked, closing modal');
        onCancel();
    };

    if (!isOpen) return null;

    return (
        <div
            className="mute-modal-overlay"
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mute-modal-title"
            aria-describedby="mute-modal-description"
        >
            <div
                className="mute-modal-container"
                onClick={(e) => e.stopPropagation()}
                ref={modalRef}
            >
                <button
                    className="mute-modal-close"
                    onClick={handleCancelClick}
                    aria-label="Close modal"
                >
                    <FiX />
                </button>

                <div className="mute-modal-header">
                    <div className="mute-modal-icon-wrapper">
                        <FiInfo className="mute-modal-icon" />
                    </div>
                    <h2 id="mute-modal-title" className="mute-modal-title">
                        Mute @{userName}?
                    </h2>
                </div>

                <div className="mute-modal-body">
                    <div id="mute-modal-description" className="mute-modal-description">
                        <p className="mute-modal-info">
                            Muting will affect your experience:
                        </p>
                        <ul className="mute-modal-effects">
                            <li>
                                <span className="effect-icon">•</span>
                                You won't see their posts and comments
                            </li>
                            <li>
                                <span className="effect-icon">•</span>
                                They can still send you messages
                            </li>
                            <li>
                                <span className="effect-icon">•</span>
                                You can unmute them anytime from settings
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mute-modal-footer">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={handleCancelClick}
                        tabIndex={0}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-mute"
                        onClick={handleConfirmClick}
                        ref={confirmButtonRef}
                        tabIndex={0}
                    >
                        Mute User
                    </button>
                </div>
            </div>
        </div>
    );
}
